import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { useSharedData } from '@/hooks/useSharedData'; // Import useSharedData
import { interviewService } from '@/services/interview.service';
import { TimeSelect } from '@/components/ui/time-select';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { X, Search, ChevronDown, Laptop, Handshake, Brain, Palette, Briefcase, Flag } from 'lucide-react';

interface AddInterviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddInterview?: (updatedInterview?: any) => void;
  interviewToEdit?: any;
  isEditMode?: boolean;
  onSuccess?: () => void;
  initialCandidateId?: string;
  existingInterviewsCount?: number;
}

export function AddInterviewDialog({
  open,
  onOpenChange,
  onAddInterview,
  interviewToEdit,
  isEditMode = false,
  onSuccess,
  initialCandidateId,
  existingInterviewsCount = 0,
}: AddInterviewDialogProps) {
  const { toast } = useToast();
  //const { hasSubModulePermission } = useAuth();
  const { departments, employees, candidates, refreshCandidates } = useSharedData(); // Use shared data for all common data

  const [form, setForm] = useState({
    candidate: '',
    interviewers: [] as string[],
    interviewType: '',
    roundName: '',
    customInterviewType: '', // for free-text when "other" is chosen
    date: null as Date | null,
    time: '',
    notes: '',
    interviewMode: 'onsite' as 'onsite' | 'virtual',
    interviewLink: '',
  });

  const [_isCustomType, setIsCustomType] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showInterviewerDropdown, setShowInterviewerDropdown] = useState(false);
  const [interviewerSearch, setInterviewerSearch] = useState('');

  // Helper function to get employee display name
  const getEmployeeDisplayName = (employee: any) => {
    if (!employee) return null; // Return null instead of 'Unknown Employee'

    // Check if employee has user object with firstName/lastName
    if (employee.user && employee.user.firstName && employee.user.lastName) {
      return `${employee.user.firstName} ${employee.user.lastName}`;
    }

    // Check if employee has firstName/lastName directly
    if (employee.firstName && employee.lastName) {
      return `${employee.firstName} ${employee.lastName}`;
    }

    // Check if employee has name field
    if (employee.name) {
      return employee.name;
    }

    // Fallback to email or ID
    return employee.email || employee._id || null;
  };

  // Helper function to check if employee is valid (not deleted)
  const isValidEmployee = (employee: any) => {
    if (!employee) return false;

    // Check if employee has any identifying information
    const hasName = (employee.user && employee.user.firstName && employee.user.lastName) ||
      (employee.firstName && employee.lastName) ||
      employee.name;

    const hasEmail = employee.email;
    const hasId = employee._id || employee.id;

    return hasName || hasEmail || hasId;
  };

  // Helper function to filter out deleted employees from selected interviewers
  const getValidSelectedInterviewers = () => {
    return form.interviewers.filter(interviewerId => {
      const employee = employees.find(emp => getEmployeeId(emp) === interviewerId);
      return employee && isValidEmployee(employee);
    });
  };

  // Update form when employees list changes to remove deleted interviewers
  useEffect(() => {
    if (form.interviewers.length > 0 && employees.length > 0) {
      const validInterviewers = getValidSelectedInterviewers();

      // If some interviewers were removed, update the form
      if (validInterviewers.length !== form.interviewers.length) {
        setForm(prev => ({
          ...prev,
          interviewers: validInterviewers
        }));
      }
    }
  }, [employees, form.interviewers]);

  // Helper function to get employee ID - ensure it returns a valid MongoDB ID
  const getEmployeeId = (employee: any) => {
    if (!employee) return '';

    // Try different possible ID fields
    const possibleIds = [
      employee._id,
      employee.id,
      employee.user?._id,
      employee.user?.id
    ];

    // Find the first valid MongoDB ID
    for (const id of possibleIds) {
      if (id && typeof id === 'string' && /^[0-9a-fA-F]{24}$/.test(id)) {
        return id;
      }
    }

    return '';
  };

  // Helper function to get candidate ID
  const getCandidateId = (candidate: any) => {
    if (!candidate) return '';

    if (typeof candidate === 'object') {
      return candidate._id || candidate.id || '';
    }

    return candidate;
  };

  // Helper function to get department ID
  const getDepartmentId = (department: any) => {
    if (!department) return '';

    if (typeof department === 'object') {
      return department._id || department.id || '';
    }

    return department;
  };

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.interviewer-dropdown')) {
        setShowInterviewerDropdown(false);
        setInterviewerSearch(''); // Clear search when closing
      }
    };

    if (showInterviewerDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showInterviewerDropdown]);

  // Filter employees based on search
  const filteredEmployees = employees.filter(employee => {
    if (!interviewerSearch.trim()) return true;

    const searchTerm = interviewerSearch.toLowerCase();
    const displayName = getEmployeeDisplayName(employee)?.toLowerCase() || ''; // Use optional chaining

    return displayName.includes(searchTerm);
  });

  // Debug: Log candidates data and ensure they're loaded
  useEffect(() => {
    if (candidates.length > 0) {
      // Candidates loaded successfully
    } else if (open) {
      refreshCandidates();
    }
  }, [candidates, open, refreshCandidates]);

  // Ensure date and time are always blank for new interviews
  useEffect(() => {
    if (isEditMode && interviewToEdit) {

      // Only set form if we have the required data loaded
      if (candidates.length > 0) {

        // Extract and validate candidate ID
        let candidateId = getCandidateId(interviewToEdit.candidate);

        if (!candidateId || !/^[0-9a-fA-F]{24}$/.test(candidateId)) {
          candidateId = candidates.length > 0 ? candidates[0]._id : '';
        }

        // Extract and validate department ID
        let departmentId = getDepartmentId(interviewToEdit.department);

        if (!departmentId || !/^[0-9a-fA-F]{24}$/.test(departmentId)) {
          departmentId = departments.length > 0 ? departments[0]._id : '';
        }

        // Extract and validate interviewer IDs
        let interviewersArray: string[] = [];

        if (interviewToEdit.interviewers && Array.isArray(interviewToEdit.interviewers)) {
          interviewersArray = interviewToEdit.interviewers
            .map((interviewer: any) => {
              const id = getEmployeeId(interviewer);
              return id;
            })
            .filter((id: string) => id && /^[0-9a-fA-F]{24}$/.test(id));
        }

        const formData = {
          candidate: candidateId,
          interviewers: interviewersArray,
          interviewType: interviewToEdit.interviewType || 'technical',
          roundName: interviewToEdit.roundName || `Round ${existingInterviewsCount}`,
          customInterviewType: '',
          date: interviewToEdit.interviewDate ? new Date(interviewToEdit.interviewDate) : null,
          time: interviewToEdit.interviewTime || '',
          notes: interviewToEdit.notes || '',
          interviewMode: ((interviewToEdit.location && interviewToEdit.location.startsWith('http')) ? 'virtual' : 'onsite') as 'onsite' | 'virtual',
          interviewLink: (interviewToEdit.location && interviewToEdit.location.startsWith('http')) ? interviewToEdit.location : '',
        };

        setForm(formData);
      }
    } else if (!isEditMode && open) { // <-- Only reset when dialog is opened for new interview
      // For new interviews
      const defaultCandidateId = initialCandidateId || (candidates.length > 0 ? candidates[0]._id : '');

      setForm({
        candidate: defaultCandidateId,
        interviewers: [], // Start with empty array
        interviewType: 'technical',
        roundName: `Round ${existingInterviewsCount + 1}`, // Auto-increment
        customInterviewType: '',
        date: null,
        time: '',
        notes: '',
        interviewMode: 'onsite' as 'onsite' | 'virtual',
        interviewLink: '',
      });
    }
  }, [isEditMode, interviewToEdit, open, candidates, departments, employees, initialCandidateId, existingInterviewsCount]);

  const validateField = (name: string, value: any): string | undefined => {
    switch (name) {
      case 'candidate':
        return value ? undefined : 'Candidate is required';
      case 'interviewers':
        return getValidSelectedInterviewers().length > 0 ? undefined : 'At least one interviewer is required';
      case 'interviewType':
        // If custom type, validate the custom field instead
        if (value === '__custom__') return form.customInterviewType.trim() ? undefined : 'Please enter a custom round name';
        return value ? undefined : 'Interview Type is required';
      case 'roundName':
        return value ? undefined : 'Round Name is required';
      case 'date':
        return value ? undefined : 'Date is required';
      case 'time':
        return value ? undefined : 'Time is required';
      case 'interviewLink':
        if (form.interviewMode === 'virtual') {
          return value ? undefined : 'Meeting link is required for virtual interviews';
        }
        return undefined;
      default:
        return undefined;
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    Object.keys(form).forEach((key) => {
      const error = validateField(key, (form as any)[key]);
      if (error) {
        newErrors[key] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' })); // Clear error on change
  };

  const handleSelectChange = (name: string, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' })); // Clear error on change
  };

  const handleDateChange = (date: Date | undefined) => {
    if (date) {
      setForm((prev) => ({ ...prev, date }));
      setErrors((prev) => ({ ...prev, date: '' })); // Clear error on change
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // MongoDB ID validation regex
      const mongoIdRegex = /^[0-9a-fA-F]{24}$/;
      let finalCandidateId = form.candidate;
      let finalInterviewers = [...form.interviewers];

      // Validate and fix candidate ID
      if (!finalCandidateId || !mongoIdRegex.test(finalCandidateId)) {
        if (candidates.length > 0) {
          finalCandidateId = candidates[0]._id;
        } else {
          toast({
            title: 'Validation Error',
            description: 'No valid candidates available.',
            variant: 'destructive',
          });
          return;
        }
      }

      // Validate and fix interviewer IDs
      const validInterviewers = finalInterviewers.filter(id => id && mongoIdRegex.test(id));
      finalInterviewers = validInterviewers;

      // Final validation - ensure we have valid MongoDB IDs
      if (!finalCandidateId || !mongoIdRegex.test(finalCandidateId)) {
        toast({
          title: 'Validation Error',
          description: 'Invalid candidate ID. Please try again.',
          variant: 'destructive',
        });
        return;
      }
      if (finalInterviewers.length === 0) {
        toast({
          title: 'Validation Error',
          description: 'At least one interviewer is required.',
          variant: 'destructive',
        });
        return;
      }
      const invalidInterviewers = finalInterviewers.filter(id => !mongoIdRegex.test(id));
      if (invalidInterviewers.length > 0) {
        toast({
          title: 'Validation Error',
          description: 'Invalid interviewer IDs. Please try again.',
          variant: 'destructive',
        });
        return;
      }

      // Resolve the final interview type (preset or custom)
      const resolvedInterviewType = form.interviewType === '__custom__'
        ? (form.customInterviewType.trim() || 'custom')
        : form.interviewType;

      const payload = {
        interviewDate: form.date ? format(form.date, 'yyyy-MM-dd') : '',
        interviewTime: form.time || '',
        candidate: finalCandidateId,
        interviewers: finalInterviewers,
        interviewType: resolvedInterviewType,
        roundName: form.roundName,
        notes: form.notes,
        location: form.interviewMode === 'virtual' ? form.interviewLink : 'Onsite',
      };



      if (isEditMode && interviewToEdit?._id) {
        // Make the server request and wait for it
        const updatedInterview = await interviewService.updateInterview(interviewToEdit._id, payload);

        // Update the UI with the server response
        onAddInterview?.(updatedInterview);

        toast({
          title: '✅ Interview Updated',
          description: 'Interview details have been successfully updated.',
        });
      } else {
        // Make the server request and wait for it
        const newInterview = await interviewService.createInterview(payload);

        // Update the UI with the server response
        onAddInterview?.(newInterview);

        toast({
          title: '✅ Interview Scheduled',
          description: 'New interview has been successfully scheduled.',
        });
      }

      // Call onSuccess after server confirmation
      onSuccess?.();

      // Close the dialog
      onOpenChange(false);
    } catch (error) {
      console.error('=== DEBUG: Error details ===');
      console.error('Error saving interview:', error);
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        console.error('Response status:', axiosError.response?.status);
        console.error('Response data:', axiosError.response?.data);
        console.error('Response message:', axiosError.response?.data?.message);
      }
      toast({
        title: '❌ Failed to save interview',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    }
  };

  const interviewTypes = [
    { value: 'technical', label: 'Technical', icon: Laptop, color: 'text-blue-500', bg: 'bg-blue-50' },
    { value: 'hr', label: 'HR Interview', icon: Handshake, color: 'text-orange-500', bg: 'bg-orange-50' },
    { value: 'behavioural', label: 'Behavioural', icon: Brain, color: 'text-pink-500', bg: 'bg-pink-50' },
    { value: 'portfolio_review', label: 'Portfolio Review', icon: Palette, color: 'text-purple-500', bg: 'bg-purple-50' },
    { value: 'management', label: 'Management Round', icon: Briefcase, color: 'text-indigo-500', bg: 'bg-indigo-50' },
    { value: 'final', label: 'Final Round', icon: Flag, color: 'text-green-600', bg: 'bg-green-50' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[600px] max-h-[90vh] border-0 shadow-2xl bg-white"
        style={{ borderRadius: '16px' }}
      >
        <DialogHeader className="bg-white" style={{ borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }}>
          <DialogTitle>{isEditMode ? 'Edit Interview' : 'Schedule New Interview'}</DialogTitle>
          <DialogDescription className="sr-only">Form to schedule or update interview details for a candidate.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 px-6 overflow-y-auto max-h-[60vh] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {/* Candidate Field */}
          <div className="space-y-2">
            <Label htmlFor="candidate" className="text-sm font-medium">
              Candidate {isEditMode && <span className="text-gray-500 text-xs">(Cannot be changed)</span>}
            </Label>
            {initialCandidateId || isEditMode ? (
              <Input
                readOnly
                value={(() => {
                  const candidate = candidates.find(c => c._id === form.candidate);
                  if (!candidate) return 'Loading...';
                  const firstName = candidate.firstName || candidate.name?.split(' ')[0] || '';
                  const lastName = candidate.lastName || candidate.name?.split(' ')[1] || '';
                  return `${firstName} ${lastName}`.trim() || 'Unknown Candidate';
                })()}
                className="bg-gray-50 cursor-not-allowed"
              />
            ) : (
              <Select
                name="candidate"
                value={form.candidate}
                onValueChange={(value) => handleSelectChange('candidate', value)}
                disabled={isEditMode}
              >
                <SelectTrigger className={cn(errors.candidate && 'border-red-500', isEditMode && 'bg-gray-100 cursor-not-allowed opacity-75')}>
                  <SelectValue placeholder="Select a candidate">
                    {(() => {
                      const candidate = candidates.find(c => c._id === form.candidate);
                      if (candidates.length === 0) return 'Loading candidates...';
                      if (!candidate) return 'Select a candidate';

                      // Handle different candidate name formats
                      const firstName = candidate.firstName || candidate.name?.split(' ')[0] || '';
                      const lastName = candidate.lastName || candidate.name?.split(' ')[1] || '';
                      const fullName = `${firstName} ${lastName}`.trim();
                      return fullName || 'Unknown Candidate';
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent position="popper">
                  {candidates.length === 0 ? (
                    <SelectItem value="" disabled>
                      Loading candidates...
                    </SelectItem>
                  ) : (
                    candidates.map((candidate) => (
                      <SelectItem key={candidate._id} value={candidate._id}>
                        {candidate.firstName || candidate.name?.split(' ')[0] || ''} {candidate.lastName || candidate.name?.split(' ')[1] || ''}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
            {errors.candidate && <p className="text-red-500 text-sm">{errors.candidate}</p>}
          </div>

          {/* Grid layout for Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            {/* Interview Date Field */}
            <div className="space-y-2">
              <Label htmlFor="date" className="text-sm font-medium">
                Interview Date <span className="text-red-500">*</span>
              </Label>
              <Input
                type="date"
                name="date"
                min={new Date().toISOString().split('T')[0]}
                value={form.date ? format(form.date, 'yyyy-MM-dd') : ''}
                onChange={(e) => {
                  const dateValue = e.target.value;
                  if (dateValue) {
                    handleDateChange(new Date(dateValue));
                  } else {
                    handleDateChange(undefined);
                  }
                }}
                className={cn(errors.date && 'border-red-500')}
              />
              {errors.date && <p className="text-red-500 text-sm">{errors.date}</p>}
            </div>

            {/* Interview Time Field */}
            <div className="space-y-2">
              <Label htmlFor="time" className="text-sm font-medium">
                Interview Time <span className="text-red-500">*</span>
              </Label>
              <TimeSelect
                value={form.time}
                onValueChange={(value: string) => handleSelectChange('time', value)}
                className={cn(errors.time && 'border-red-500')}
              />
              {errors.time && <p className="text-red-500 text-sm">{errors.time}</p>}
            </div>
          </div>

          {/* Grid layout for Round Name and Interview Type */}
          <div className="grid grid-cols-2 gap-4">
            {/* Round Name Field */}
            <div className="space-y-2">
              <Label htmlFor="roundName" className="text-sm font-medium">
                Round Name <span className="text-red-500">*</span>
              </Label>
              <Input
                name="roundName"
                placeholder="e.g. Round 1"
                value={form.roundName}
                onChange={handleChange}
                className={cn(errors.roundName && 'border-red-500')}
              />
              {errors.roundName && <p className="text-red-500 text-sm">{errors.roundName}</p>}
            </div>

            {/* Interview Type Field */}
            <div className="space-y-2">
              <Label htmlFor="interviewType" className="text-sm font-medium">
                Interview Type <span className="text-red-500">*</span>
              </Label>
              <Select
                name="interviewType"
                value={form.interviewType}
                onValueChange={(value) => {
                  handleSelectChange('interviewType', value);
                  setIsCustomType(value === '__custom__');
                  if (value !== '__custom__') {
                    setForm(prev => ({ ...prev, customInterviewType: '' }));
                  }
                }}
              >
                <SelectTrigger className={cn(errors.interviewType && 'border-red-500', "h-10")}>
                  <SelectValue placeholder="Select interview type">
                    {(() => {
                      const selected = interviewTypes.find(t => t.value === form.interviewType);
                      if (!selected) return 'Select interview type';
                      const Icon = selected.icon;
                      return (
                        <div className="flex items-center gap-2">
                          <div className={cn("flex items-center justify-center p-1 rounded-md", selected.bg)}>
                            <Icon className={cn("h-4 w-4", selected.color)} />
                          </div>
                          <span className="font-medium text-gray-700">{selected.label}</span>
                        </div>
                      );
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent position="popper">
                  {interviewTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <SelectItem key={type.value} value={type.value} className="py-2">
                        <div className="flex items-center gap-2">
                          <div className={cn("flex items-center justify-center p-1.5 rounded-md", type.bg)}>
                            <Icon className={cn("h-4 w-4", type.color)} />
                          </div>
                          <span className="font-medium text-gray-700">{type.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {errors.interviewType && <p className="text-red-500 text-sm">{errors.interviewType}</p>}

              {/* Custom type input - only shown when "Custom" is selected */}

            </div>
          </div>

          {/* Interviewers Field */}
          <div className="space-y-2">
            <Label htmlFor="interviewers" className="text-sm font-medium">
              Interviewers <span className="text-red-500">*</span>
            </Label>

            {/* Selected Interviewers Display */}
            {form.interviewers.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3 p-3 bg-gray-50 rounded-md border">
                <div className="w-full mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Selected ({getValidSelectedInterviewers().length}):
                  </span>
                </div>
                {getValidSelectedInterviewers().map((interviewerId) => {
                  const employee = employees.find(emp => getEmployeeId(emp) === interviewerId);
                  const displayName = getEmployeeDisplayName(employee);

                  // Only show if employee is valid and has a display name
                  if (!employee || !displayName) return null;

                  return (
                    <Badge key={interviewerId} variant="secondary" className="flex items-center gap-1 px-3 py-1">
                      <span className="text-sm">{displayName}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setForm(prev => ({
                            ...prev,
                            interviewers: prev.interviewers.filter(id => id !== interviewerId)
                          }));
                          setErrors(prev => ({ ...prev, interviewers: '' }));
                        }}
                        className="ml-1 hover:bg-gray-300 rounded-full p-0.5 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}

            {/* Multi-select Dropdown */}
            <div className="relative interviewer-dropdown">
              <button
                type="button"
                onClick={() => {
                  setShowInterviewerDropdown(!showInterviewerDropdown);
                  if (!showInterviewerDropdown) {
                    setInterviewerSearch(''); // Clear search when opening
                  }
                }}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2.5 border rounded-md bg-white text-sm transition-colors",
                  errors.interviewers && 'border-red-500',
                  !form.interviewers.length && 'text-gray-500',
                  showInterviewerDropdown && 'border-blue-500 ring-1 ring-blue-500'
                )}
              >
                <span className="flex items-center gap-2">
                  <span>
                    {getValidSelectedInterviewers().length > 0
                      ? `${getValidSelectedInterviewers().length} interviewer(s) selected`
                      : "Select Interviewers"
                    }
                  </span>
                </span>
                <ChevronDown className={cn(
                  "w-4 h-4 transition-transform",
                  showInterviewerDropdown && "rotate-180"
                )} />
              </button>

              {showInterviewerDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-hidden">
                  {/* Search Input */}
                  <div className="p-3 border-b border-gray-100 bg-gray-50">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search employees..."
                        value={interviewerSearch}
                        onChange={(e) => setInterviewerSearch(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Employee List */}
                  <div className="max-h-60 overflow-y-auto">
                    {filteredEmployees.length > 0 ? (
                      <div>
                        <div className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50 border-b">
                          {filteredEmployees.length} employee(s) found
                        </div>
                        {filteredEmployees.map((employee) => {
                          const employeeId = getEmployeeId(employee);
                          const isSelected = form.interviewers.includes(employeeId);

                          return (
                            <label
                              key={employeeId}
                              className="flex items-center px-3 py-2.5 hover:bg-blue-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {
                                  setForm(prev => ({
                                    ...prev,
                                    interviewers: isSelected
                                      ? prev.interviewers.filter(id => id !== employeeId)
                                      : [...prev.interviewers, employeeId]
                                  }));
                                  setErrors(prev => ({ ...prev, interviewers: '' }));
                                }}
                                className="mr-3 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {getEmployeeDisplayName(employee)}
                                </div>
                                {employee.email && (
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {employee.email}
                                  </div>
                                )}
                              </div>
                              {isSelected && (
                                <div className="ml-2">
                                  <Badge variant="secondary" className="text-xs">
                                    Selected
                                  </Badge>
                                </div>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="px-3 py-4 text-center">
                        <div className="text-sm text-gray-500">
                          {interviewerSearch.trim()
                            ? `No employees found matching "${interviewerSearch}"`
                            : 'No employees available'
                          }
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer with quick actions */}
                  {getValidSelectedInterviewers().length > 0 && (
                    <div className="p-3 border-t border-gray-100 bg-gray-50">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">
                          {getValidSelectedInterviewers().length} selected
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setForm(prev => ({ ...prev, interviewers: [] }));
                            setErrors(prev => ({ ...prev, interviewers: '' }));
                          }}
                          className="text-red-600 hover:text-red-700 font-medium"
                        >
                          Clear all
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {errors.interviewers && <p className="text-red-500 text-sm">{errors.interviewers}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="interviewMode" className="text-sm font-medium">
                Interview Mode <span className="text-red-500">*</span>
              </Label>
              <Select
                name="interviewMode"
                value={form.interviewMode}
                onValueChange={(value: 'onsite' | 'virtual') => setForm(prev => ({ ...prev, interviewMode: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="onsite">Onsite</SelectItem>
                  <SelectItem value="virtual">Virtual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.interviewMode === 'virtual' && (
              <div className="space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                <Label htmlFor="interviewLink" className="text-sm font-medium">
                  Interview Link <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="interviewLink"
                  name="interviewLink"
                  placeholder="Paste meeting link here..."
                  value={form.interviewLink}
                  onChange={handleChange}
                  className={cn(errors.interviewLink && 'border-red-500')}
                />
                {errors.interviewLink && <p className="text-red-500 text-[10px] font-bold uppercase">{errors.interviewLink}</p>}
              </div>
            )}
          </div>

          {/* Notes Field */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-sm font-medium">
              Notes
            </Label>
            <Textarea
              id="notes"
              name="notes"
              placeholder="Add any additional notes about the interview..."
              value={form.notes}
              onChange={handleChange}
              className="min-h-[100px] resize-none"
            />
          </div>
        </div>
        <div
          className="flex justify-end gap-2 pt-4 border-t bg-white"
          style={{ borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px' }}
        >
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>
            {isEditMode ? 'Update Interview' : 'Schedule Interview'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
} 